import React, { useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Home,
  LayoutGrid,
  MessageCircle,
  FileText,
  Bell,
  Menu,
  LogOut,
} from "lucide-react";
import { apiRequest } from "../../Api/Api";
import { resetSocketConnection } from "../../Api/socket";
import { ENDPOINTS } from "../../Api/Endpoints";
import { tokenManager } from "../../Api/tokenManager";
import { logout } from "../../Features/Postes/Slice/authSlice";
import { clearPatientUploadFlowState } from "../../utils/patientUploadFlowSession";
import PatientAppBar from "../PatientAppBar/PatientAppBar";
import { showConfirm } from "../../feedback/feedbackApi";
import { getPatientShellScreenTitle } from "./patientShellScreenTitle";
import { usePatientShellChrome } from "../../Layout/patientShellChrome";
import "./PatientAppLayout.scss";

const NAV = [
  { key: "dashboard", label: "My care", icon: Home, to: "/HealBidDashboard" },
  { key: "requests", label: "List", icon: LayoutGrid, to: "/patient-requests" },
  { key: "messages", label: "Messages", icon: MessageCircle, to: "/patient-chats" },
  { key: "documents", label: "Documents", icon: FileText, to: "/patient-documents" },
  { key: "notifications", label: "Notifications", icon: Bell, to: "/patient-notifications" },
  { key: "profile", label: "Menu", icon: Menu, to: "/patient-profile" },
];

function pathMatchesActive(key, p) {
  if (key === "dashboard")
    return (
      p === "/HealBidDashboard" ||
      p === "/HealBidDashboard/" ||
      p === "/appointment" ||
      p === "/appointment-confirmed" ||
      p === "/budget-shared-success"
    );
  if (key === "requests")
    return (
      p === "/patient-requests" ||
      p.startsWith("/request/")
    );
  if (key === "messages") return p.startsWith("/patient-chats");
  if (key === "documents") return p.startsWith("/patient-documents");
  if (key === "notifications") return p.startsWith("/patient-notifications");
  if (key === "profile") return p === "/patient-profile" || p.startsWith("/patient-profile/");
  return false;
}

/**
 * Desktop sidebar + help + app bar; mobile relies on PatientAppShell bottom nav.
 */
export default function PatientAppLayout({
  children,
  activeNav = null,
  messageBadgeCount = 0,
  showAppBarHeadline = false,
  appBarTitle,
  appBarSubtitle,
  /** When set, overrides route-derived label in the top app bar */
  toolbarTitle,
  backTo,
  backLabel,
  /** Request wizard: when global app bar is hidden on mobile, keep hub gradient (not chat beige) + safe area */
  wizardImmersive = false,
  /** Narrow shell: optional control rendered in the app bar end slot (e.g. edit on “Your details”). */
  appBarEndSlot = null,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const p = pathname.replace(/\/$/, "") || "/";
  const handleLogout = useCallback(async () => {
    const confirmed = await showConfirm("Are you sure you want to log out?", {
      title: "Log out?",
      confirmText: "Log out",
      cancelText: "Stay",
      danger: true,
    });
    if (!confirmed) return;
    try {
      const tok = tokenManager.getTokenForRole("patient");
      if (tok) {
        await apiRequest({ method: "POST", url: ENDPOINTS.PATIENT_LOGOUT }).catch(() => {});
      }
    } finally {
      clearPatientUploadFlowState();
      dispatch(logout());
      resetSocketConnection();
      navigate("/", { replace: true });
    }
  }, [dispatch, navigate]);

  const screenTitle = useMemo(
    () => (toolbarTitle != null && String(toolbarTitle).trim() ? String(toolbarTitle).trim() : getPatientShellScreenTitle(pathname)),
    [toolbarTitle, pathname]
  );

  const { appBarHidden, appBarScrollCollapsed } = usePatientShellChrome();
  const patientChatsRoute = p.startsWith("/patient-chats");
  const patientDocumentsRoute = p.startsWith("/patient-documents");
  const patientSupportRoute = p.startsWith("/patient-support");
  const patientNotificationsRoute = p.startsWith("/patient-notifications");
  /** Always show app bar on mobile when chrome is enabled — native-style title + actions (wizard/thread hide via context). */
  const showPatientAppBar = !appBarHidden;

  const plainCanvas = activeNav === "profile";

  return (
    <div
      className={`paLayout paLayout--healbid${plainCanvas ? " paLayout--plainCanvas" : ""}${
        appBarHidden ? " paLayout--appBarHidden" : ""
      }${appBarScrollCollapsed && !appBarHidden ? " paLayout--mobileAppBarScrollCollapsed" : ""}${
        wizardImmersive ? " paLayout--wizardImmersive" : ""
      }${
        patientChatsRoute && !appBarHidden ? " paLayout--patientChatsList" : ""
      }${patientDocumentsRoute && !appBarHidden ? " paLayout--patientDocumentsEdge" : ""}${
        patientSupportRoute && !appBarHidden ? " paLayout--patientSupportEdge" : ""
      }`.trim()}
    >
      {showPatientAppBar ? (
        <PatientAppBar
          showBrand
          backTo={backTo}
          backLabel={backLabel}
          screenTitle={screenTitle}
          title={showAppBarHeadline ? appBarTitle : undefined}
          subtitle={showAppBarHeadline ? appBarSubtitle : undefined}
          scrollCollapsed={Boolean(appBarScrollCollapsed)}
          screenTitleEnd={appBarEndSlot}
        />
      ) : null}

      <div
        className={`paLayout__shell${patientChatsRoute ? " paLayout__shell--patientChats" : ""}${
          patientDocumentsRoute ? " paLayout__shell--patientDocuments" : ""
        }${patientSupportRoute ? " paLayout__shell--patientSupport" : ""}${
          patientNotificationsRoute ? " paLayout__shell--patientNotifications" : ""
        }`.trim()}
      >
        <aside className="paLayout__side" aria-label="Patient navigation">
          <nav className="paLayout__navCard">
            {NAV.map((it) => {
              const isActive = activeNav ? it.key === activeNav : pathMatchesActive(it.key, p);
              const Icon = it.icon;
              return (
                <button
                  key={it.key}
                  type="button"
                  className={`paLayout__navItem ${isActive ? "is-active" : ""}`.trim()}
                  onClick={() => navigate(it.to)}
                >
                  <span className="paLayout__navIcon" aria-hidden>
                    <Icon size={20} strokeWidth={isActive ? 2 : 1.75} />
                  </span>
                  <span className="paLayout__navLabel">{it.label}</span>
                  {it.key === "messages" && messageBadgeCount > 0 ? (
                    <span className="paLayout__navBadge">{messageBadgeCount > 9 ? "9+" : messageBadgeCount}</span>
                  ) : null}
                </button>
              );
            })}
          </nav>

          <div className="paLayout__helpCard">
            <p className="paLayout__helpKicker">Need help?</p>
            <p className="paLayout__helpText">
              Our care team responds within 30 minutes during clinic hours.
            </p>
            <button
              type="button"
              className="paLayout__helpBtn"
              onClick={() => navigate("/patient-support")}
            >
              Contact support
            </button>
          </div>

          <button type="button" className="paLayout__logoutCard" onClick={handleLogout}>
            <LogOut size={18} strokeWidth={1.75} aria-hidden />
            <span>Log out</span>
          </button>
        </aside>

        <main className="paLayout__main">{children}</main>
      </div>
    </div>
  );
}
